// FILE: packages/components/src/sprint/DayCard.tsx

import React from 'react';
import { motion } from 'framer-motion';
import { Badge, VisualElement } from '@dopomogai/ui';
import { Check } from 'lucide-react';
import clsx from 'clsx';
// For a richer experience, consider adding a markdown renderer:
// import ReactMarkdown from 'react-markdown'; 
// import remarkGfm from 'remark-gfm';

/**
 * The props for the DayCard, designed to be populated by the dynamic
 * `useSprintDashboard` hook.
 */
export interface DayCardProps {
  dayNumber: number;
  title: string;
  date: string; // Already formatted date string
  status: 'COMPLETE' | 'IN_PROGRESS' | 'AWAITING';

  // Direct data props from the backend model
  debrief?: string; // Mapped from day.summary
  links?: Array<{ text: string; url: string }>; // Mapped from day.media
  
  // A checklist generated from the day's completed tasks
  checklist?: Array<{ text: string; isComplete: boolean }>;

  // Props for translated status/content text
  statusText: { complete: string; inProgress: string; awaiting: string; };
  contentText: { inProgress: string; awaiting: string; };
}

export const DayCard: React.FC<DayCardProps> = ({ 
    dayNumber, title, date, status, 
    statusText, contentText,
    debrief, links = [], checklist = []
}) => {

  const getStatusBadge = () => {
    switch (status) {
      case 'COMPLETE':
        return <Badge text={statusText.complete} accentColor="green" />;
      case 'IN_PROGRESS':
        return <Badge text={statusText.inProgress} accentColor="blue" icon="loader" />;
      case 'AWAITING':
      default:
        return <Badge text={statusText.awaiting} accentColor="gray" />;
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
  };

  return (
    <motion.div
      variants={cardVariants}
      className="bg-white p-6 rounded-xl border-2 border-black space-y-4"
    >
      <header className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <h3 className="text-xl md:text-2xl font-bold text-gray-900">
          Day {dayNumber} - {title}
        </h3>
        {getStatusBadge()}
      </header>
      <p className="text-sm font-medium text-slate-500">{date}</p>
      
      {status === 'COMPLETE' && (
        <div className="space-y-6 pt-4 border-t-2 border-slate-100">
          {debrief && (
            <div>
              <h4 className="font-semibold text-gray-800 mb-2">Daily Debrief</h4>
              <div className="prose prose-sm prose-slate max-w-none">
                 {/* For rich text, wrap debrief in <ReactMarkdown remarkPlugins={[remarkGfm]}>{debrief}</ReactMarkdown> */}
                 <p>{debrief}</p>
              </div>
            </div>
          )}

          {/* --- UPDATED LINKS SECTION --- */}
          {links.length > 0 && (
             <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
               {links.map((link, i) => (
                 <a 
                   key={i} 
                   href={link.url} 
                   target="_blank" 
                   rel="noopener noreferrer" 
                   className="block group focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-4 focus-visible:ring-emerald-500 rounded-lg"
                 >
                   {/* This div now contains the "lift & shift" hover effect classes */}
                   <div className={clsx(
                      "aspect-video rounded-lg border-2 border-black overflow-hidden",
                      "transition-all duration-200 transform",
                      // On group hover, apply the transform and shadow
                      "group-hover:-translate-x-1 group-hover:-translate-y-1",
                      "group-hover:shadow-[4px_4px_0_0_#4f46e5]", 
                      // On group active (click), reset the transform and shadow
                      "group-active:translate-x-0 group-active:translate-y-0 group-active:shadow-none"
                   )}>
                     {/* The VisualElement now renders the video player directly inside the card */}
                     <VisualElement visual={{ type: 'video_url', url: link.url }} />
                   </div>
                   <p className="text-sm font-semibold mt-3 text-center text-slate-800 group-hover:text-indigo-600 transition-colors">
                     {link.text}
                   </p>
                 </a>
               ))}
             </div>
          )}
          
          {checklist.length > 0 && (
            <div className="pt-4 border-t border-slate-100">
              <h4 className="font-semibold text-gray-800 mb-3">Work Completed This Day</h4>
              <ul className="space-y-2">
                {checklist.filter(item => item.isComplete).map((item, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check size={16} className="text-emerald-500 shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item.text}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {status === 'IN_PROGRESS' && (
        <p className="text-slate-600 font-medium py-4">{contentText.inProgress}</p>
      )}

      {status === 'AWAITING' && (
        <p className="text-slate-500 py-4">{contentText.awaiting}</p>
      )}
    </motion.div>
  );
};